import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApiMutation, useOrg } from "@/lib/api";
import { type FormEvent, useId, useState } from "react";

const MAX_NAME = 64;

/**
 * Renders the "Name your organization" card when the signed-in user has no
 * memberships, blocking the rest of the authenticated tree. Wardkey is
 * org-scoped end-to-end (every secret, audit entry, KEK lives under an
 * org_id), so a user without an org has nothing meaningful to do until
 * they create one.
 *
 * During the initial membership load the gate renders nothing so downstream
 * routes that call `useActiveOrg()` don't mount before the org is resolved.
 * After a successful create, `refresh()` re-runs the membership fetch and
 * the gate hands control back to the normal app.
 */
export function CreateOrgGate({ children }: { children: React.ReactNode }) {
  const { needsOrg, isLoading } = useOrg();
  if (isLoading) return <GateLoading />;
  if (needsOrg) return <CreateOrgCard />;
  return <>{children}</>;
}

function GateLoading() {
  return (
    <div className="mx-auto max-w-xl px-4 py-12 lg:py-20" aria-busy="true">
      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      <div className="mt-6 h-10 w-3/4 animate-pulse rounded bg-muted" />
      <div className="mt-4 h-3 w-2/3 animate-pulse rounded bg-muted/60" />
    </div>
  );
}

function CreateOrgCard() {
  const { refresh } = useOrg();
  const [name, setName] = useState("");
  const inputId = useId();

  const create = useApiMutation<{ name: string }, unknown>(
    (client, { name: orgName }) => client.orgs.create({ name: orgName }),
    {
      onSuccess: () => {
        // Re-fetch memberships; the gate will fall through to the real app
        // once the new org shows up.
        refresh();
      },
    },
  );

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= MAX_NAME && !create.isPending;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate({ name: trimmed });
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-12 lg:py-20">
      <div className="fade-up">
        <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          <span className="dot-pulse mr-2 inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground align-middle" />
          first step
        </span>
        <h1 className="mt-4 font-sans text-[32px] font-medium leading-[1.1] tracking-tight text-foreground sm:text-4xl">
          Name your organization.
        </h1>
        <p className="mt-4 text-[14px] leading-relaxed text-muted-foreground">
          Secrets, audit entries, and team members all live under an organization. You can rename it
          later from settings.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <label
              htmlFor={inputId}
              className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
            >
              organization name
            </label>
            <Input
              id={inputId}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_NAME}
              required
              autoComplete="organization"
              placeholder="Acme"
              className="mt-2 h-11 text-[14px]"
            />
          </div>
          <Button type="submit" disabled={!canSubmit}>
            {create.isPending ? "Creating…" : "Create organization"}
            <span aria-hidden>→</span>
          </Button>
        </form>
      </div>
    </div>
  );
}
